import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import {
  appSumoPlanPrices,
  hasAppSumoPreviewParams,
  readAppSumoPreviewParams,
  toOfferPreview,
  useAppSumoUpgradeFlag,
  type AppSumoPlanPrices,
  type AppSumoPreviewParams,
} from '@/lib/appsumo-upgrade';
import {
  useGetAppSumoUpgradeOfferQuery,
  type AppSumoUpgradeOffer,
  type AppSumoUpgradePhase,
} from '@/store/features/appsumo.feature';

/** How often an open tab re-reads the offer, so the final days and the close
 *  reach a session that stays open across them. */
const OFFER_REFRESH_MS = 15 * 60 * 1000;

/** The longest delay setTimeout keeps (about 24.8 days); a later deadline is
 *  waited for in steps. */
const MAX_TIMER_MS = 2 ** 31 - 1;

export interface AppSumoUpgradeState {
  offer: AppSumoUpgradeOffer | undefined;
  /** The offer's Starter and Pro prices; null without an offer that has
   *  both. Never null while `active`. */
  prices: AppSumoPlanPrices | null;
  /** Show the offer: flag on, an AppSumo-only org the server calls
   *  eligible, both plans priced, and the price still open. */
  active: boolean;
  phase: AppSumoUpgradePhase | null;
  /** The offer is on its way; a surface that swaps on it should wait. */
  isLoading: boolean;
  /** The campaign ran for this AppSumo-only org and its price has closed:
   *  the "after Nov 13" fallbacks. */
  isClosedForAppSumo: boolean;
  /** The request failed. A 404 means the org is flagged out server-side. */
  error: unknown;
  /** DEV only: `?previewTier=...` and friends are on the URL. */
  previewing: boolean;
  preview: AppSumoPreviewParams;
  refetch: () => void;
}

/**
 * The AppSumo upgrade offer for the current org, for every campaign surface.
 *
 * Most of these surfaces mount for every user (the banner is on every
 * screen), so the request has to be earned: it runs only with the flag on and
 * for an AppSumo-only org (from the entitlements every screen already holds),
 * or with dev preview params. Anyone else makes zero extra requests.
 * `includeStacked` also asks for an AppSumo org with a paid plan on top, so
 * the upgrade page can name the plan it has, or move it from Starter to Pro.
 */
export function useAppSumoUpgrade({
  includeStacked = false,
}: { includeStacked?: boolean } = {}): AppSumoUpgradeState {
  const flag = useAppSumoUpgradeFlag();
  return useAppSumoOffer({ includeStacked, flag });
}

/**
 * The member price for the plan wizard and the Plans & billing card, which
 * follow the server's answer alone: an AppSumo-only org always asks, and the
 * server 404s the offer when the campaign is off (read as regular prices).
 * No PostHog read, so a blocked or slow PostHog can't sell an eligible member
 * the regular price, or flash it before the member price.
 */
export function useAppSumoMemberOffer(): AppSumoUpgradeState {
  return useAppSumoOffer({ includeStacked: false, flag: true });
}

function useAppSumoOffer({
  includeStacked,
  flag,
}: {
  includeStacked: boolean;
  flag: boolean;
}): AppSumoUpgradeState {
  const { isAppSumo, isAppSumoOnly, isReady, organizationId } =
    useFeatureAccess();
  const preview = readAppSumoPreviewParams();
  const previewing = hasAppSumoPreviewParams(preview);
  const audience =
    previewing || (isReady && (isAppSumoOnly || (includeStacked && isAppSumo)));
  const skip = !organizationId || !(flag || previewing) || !audience;

  const { currentData, isError, error, refetch } =
    useGetAppSumoUpgradeOfferQuery(
      { organizationId, preview: toOfferPreview(preview) },
      { skip, refetchOnFocus: true, pollingInterval: OFFER_REFRESH_MS }
    );
  // A 404 is the server leaving this org out (the campaign off): no offer,
  // even when RTK still holds an earlier answer, and through the next
  // refetch, which keeps the error until it answers. Any other failure keeps
  // the offer already shown; the server still gates checkout.
  const flaggedOut =
    (error as { status?: unknown } | undefined)?.status === 404;
  const offer = flaggedOut ? undefined : currentData;

  // The server's phase, except that a deadline this browser has watched pass
  // closes the offer before the next refetch says so.
  const endsAt = offer ? Date.parse(offer.ends_at) : Number.NaN;
  useRerenderAt(skip ? Number.NaN : endsAt);
  const phase: AppSumoUpgradePhase | null = !offer
    ? null
    : Number.isFinite(endsAt) && endsAt <= Date.now()
      ? 'closed'
      : offer.phase;
  const forThisOrg = !skip && (isAppSumoOnly || previewing);
  const prices = useMemo(
    () => (skip ? null : appSumoPlanPrices(offer)),
    [skip, offer]
  );
  // Every surface words the deadline and both prices; an offer without them
  // (a contract break) is no offer rather than a crash in the app shell.
  const showable = !!offer?.eligible && Number.isFinite(endsAt) && !!prices;

  const refetchOffer = useCallback(() => {
    if (!skip) void refetch();
  }, [skip, refetch]);

  return {
    offer: skip ? undefined : offer,
    prices,
    active: forThisOrg && showable && phase !== null && phase !== 'closed',
    phase: skip ? null : phase,
    isLoading: !skip && offer === undefined && !isError && !flaggedOut,
    isClosedForAppSumo: forThisOrg && phase === 'closed',
    error: skip ? undefined : error,
    previewing,
    preview,
    refetch: refetchOffer,
  };
}

/**
 * Renders again once `at` (ms since the epoch) has passed, so a page left
 * open across the deadline drops the offer without waiting for a refetch.
 */
function useRerenderAt(at: number) {
  const [, setPassed] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(at)) return;
    let timer: number | undefined;
    const wait = () => {
      const left = at - Date.now();
      if (left < 0) return;
      timer = window.setTimeout(
        () => {
          if (Date.now() >= at) setPassed(at);
          else wait();
        },
        Math.min(left, MAX_TIMER_MS)
      );
    };
    wait();
    return () => window.clearTimeout(timer);
  }, [at]);
}
